import React, { useCallback, useRef, useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import {
    Column,
    DataGrid,
    Selection,
    Export,
    Sorting,
    Paging,
    Toolbar,
    Item,
    Editing,
    FilterRow,
    Lookup,
    RequiredRule
} from "devextreme-react/data-grid";
import DataSource from "devextreme/data/data_source";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import { Button } from 'devextreme-react/button';
import { LoadPanel } from 'devextreme-react/load-panel';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import axios from "axios";
import routes from "../../../../../../assets/apiRoutes.json";
import { EMPTY_GUID, CommissionBasisType, CommissionMessages } from "../../../../../../enum/Enum";

// SPOPENITEMS.BASISTYPE — how Amount is derived from Rate and Basis (OMD0000 edtBasisTypeChange).
const basisTypeOptions = [
    { id: CommissionBasisType.Percentage, text: 'Percentage' },
    { id: CommissionBasisType.PerUnit, text: 'Per Unit' },
    { id: CommissionBasisType.Flat, text: 'Flat' },
];

// Committed commission rows (U/Z) are read-only per row (OMD0000 SetCommissionsEnabledDisabledFields).
const committedRecordTypes = ['U', 'Z'];
const isCommittedRow = (row) => committedRecordTypes.includes(row?.RecordType);

// Client-side mirror of the server ComputeCommissionAmount so the Amount cell updates live.
function computeAmount(basisType, basis, rate) {
    const r = Number(rate) || 0;
    const b = Number(basis) || 0;
    if (basisType === CommissionBasisType.Percentage) return (r / 100) * b;
    if (basisType === CommissionBasisType.Flat) return r;
    return r * b; // Per Unit / default
}

function CommissionsTab(props) {
    const accessToken = props?.oidc?.user?.access_token;
    // The currently selected transaction-detail line owns these commission rows.
    const detailRowId = props.formFields?.Rowid;
    const hasDetail = !!detailRowId && detailRowId !== EMPTY_GUID;
    // Committed orders are read-only (OMD0000: grid greyed when header STATUS = 'Z').
    const isReadOnly = !!props.isReadOnly;
    const canEdit = hasDetail && !isReadOnly;

    const commissionGrid = useRef(null);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const hideLoadPanel = useCallback(() => setLoadPanelVisible(false), []);

    // Participant (salesperson) pick-list — reused from the Sales Order lookup endpoint.
    useEffect(() => {
        if (!accessToken) return;
        let cancelled = false;
        axios
            .get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetSalePersons}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            })
            .then((res) => {
                if (cancelled) return;
                const list = res.data?.Lookups ?? res.data?.lookups ?? [];
                setParticipants(
                    list.map((p) => ({
                        rowid: p.Rowid ?? p.rowid,
                        code: (p.Code ?? p.code ?? "").trim(),
                    }))
                );
            })
            .catch(() => {
                if (!cancelled) setParticipants([]);
            });
        return () => {
            cancelled = true;
        };
    }, [accessToken]);

    const dataSource = useMemo(() => {
        if (!hasDetail) {
            return new DataSource({ store: [], key: "RowId" });
        }
        return new DataSource({
            store: createStore({
                key: "RowId",
                loadUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.GetOpenItems}`,
                insertUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.AddOpenItem}`,
                updateUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.EditOpenItem}`,
                deleteUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.DeleteOpenItem}`,
                loadParams: { detailRowId },
                onBeforeSend: function (operation, ajaxOptions) {
                    // Standard DevExtreme protocol: the grid posts form-encoded values/key.
                    // Key + parent FK are stamped in onRowInserting/onRowUpdating, not here.
                    ajaxOptions.headers = {
                        Authorization: `Bearer ${accessToken}`,
                        access_token: accessToken,
                    };
                    // Show the full-screen loader for the duration of an add/edit/delete round-trip.
                    if (operation === "insert" || operation === "update" || operation === "delete") {
                        setLoadPanelVisible(true);
                    }
                },
                // Fires after any insert/update/delete resolves — clear the loader.
                onModified: function () {
                    setLoadPanelVisible(false);
                },
                errorHandler: function () {
                    setLoadPanelVisible(false);
                },
                // The API returns the full row (with computed Amount + Cumulative), so DevExtreme
                // patches in place (refreshMode="repaint") — no manual grid.refresh() needed.
                onInserted: function () {
                    notify(CommissionMessages.Added, "success", 3000);
                },
                onUpdated: function () {
                    notify(CommissionMessages.Updated, "success", 3000);
                },
                onRemoved: function () {
                    notify(CommissionMessages.Deleted, "success", 3000);
                },
            }),
            key: "RowId",
            paginate: false,
        });
    }, [detailRowId, hasDetail, accessToken]);

    const onSelectionChanged = useCallback((e) => {
        setSelectedKeys(e.selectedRowKeys);
    }, []);

    const onInitNewRow = useCallback((e) => {
        e.data.RowId = EMPTY_GUID;
        e.data.RSourceId = detailRowId;
        e.data.BasisType = CommissionBasisType.PerUnit;
        e.data.Basis = 0;
        e.data.Rate = 0;
        e.data.Amount = 0;
        e.data.Description = '';
    }, [detailRowId]);

    // Stamp the key + parent FK so they land inside the posted "values" JSON.
    const onRowInserting = useCallback((e) => {
        e.data.RowId = EMPTY_GUID;
        e.data.RSourceId = detailRowId;
    }, [detailRowId]);

    // Cell editing sends only the changed cell — merge so unchanged columns aren't wiped.
    const onRowUpdating = useCallback((e) => {
        e.newData = { ...e.oldData, ...e.newData };
        e.newData.RSourceId = detailRowId;
    }, [detailRowId]);

    // On participant select, auto-fill Basis Type / Basis / Rate / Amount from the SP settlement-payables
    // setup (OMD0000 edtP_ParticipantExit -> CalculateSPCommissions). If no setup matches, manual entry stays.
    const setParticipantValue = useCallback(async (newData, value) => {
        newData.RIdentity = value;
        if (!value || !hasDetail || !accessToken) return;
        try {
            const res = await axios.get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetCommissionDefaults}`, {
                params: { detailRowId, participantRowId: value },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const d = res.data;
            if (d && d.BasisType) {
                newData.BasisType = d.BasisType;
                newData.Basis = d.Basis ?? 0;
                newData.Rate = d.Rate ?? 0;
                newData.Amount = d.Amount ?? computeAmount(d.BasisType, d.Basis, d.Rate);
            }
        } catch {
            // No settlement setup / lookup failed — keep manual entry.
        }
    }, [detailRowId, hasDetail, accessToken]);

    // Recompute Amount live when Basis Type / Basis / Rate change (OMD0000 parity).
    const setBasisTypeValue = useCallback((newData, value, currentRow) => {
        newData.BasisType = value;
        newData.Amount = computeAmount(value, currentRow.Basis, currentRow.Rate);
    }, []);
    const setBasisValue = useCallback((newData, value, currentRow) => {
        newData.Basis = value;
        newData.Amount = computeAmount(currentRow.BasisType, value, currentRow.Rate);
    }, []);
    const setRateValue = useCallback((newData, value, currentRow) => {
        newData.Rate = value;
        newData.Amount = computeAmount(currentRow.BasisType, currentRow.Basis, value);
    }, []);

    const onDeleteSelected = useCallback(() => {
        const grid = commissionGrid.current?.instance?.();
        if (!grid) return;
        const keys = grid.getSelectedRowKeys();
        if (!keys.length) return;
        confirm("Delete selected record?", "Confirm delete").then((yes) => {
            if (!yes) return;
            const store = grid.getDataSource().store();
            Promise.allSettled(keys.map((k) => store.remove(k))).then((results) => {
                results
                    .filter((r) => r.status === "rejected")
                    .forEach((r) => {
                        const error = r.reason;
                        const message =
                            (typeof error === "string" ? error : null) ||
                            error?.message ||
                            error?.responseJSON?.message ||
                            CommissionMessages.DeleteFailed;
                        notify(message, "error", 5000);
                    });
                grid.refresh();
            });
        });
    }, []);

    // Disable editing per row for committed lines (U/Z) even inside an open order (OMD0000 parity).
    const onEditorPreparing = useCallback((e) => {
        if (e.parentType === "dataRow" && isCommittedRow(e.row?.data)) {
            e.editorOptions.disabled = true;
        }
    }, []);

    return (
        <div style={{ height: '100%', overflow: 'auto' }}>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                position={{ of: window }}
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
            />
            <DataGrid
                dataSource={dataSource}
                ref={commissionGrid}
                selectedRowKeys={selectedKeys}
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                onSelectionChanged={onSelectionChanged}
                onInitNewRow={onInitNewRow}
                onRowInserting={onRowInserting}
                onRowUpdating={onRowUpdating}
                onEditorPreparing={onEditorPreparing}
                height="100%"
            >
                <Selection mode="multiple" showCheckBoxesMode="always" allowSelectAll={false} />
                <Export enabled={true} allowExportSelectedData={true} />
                <Editing
                    mode="cell"
                    refreshMode="repaint"
                    allowAdding={canEdit}
                    allowUpdating={canEdit}
                    allowDeleting={false}
                />
                <FilterRow visible={true} />
                <Sorting mode="none" />
                <Paging enabled={false} />

                <Column dataField="RowId" caption="Row ID" visible={false} showInColumnChooser={false} />
                <Column dataField="RSourceId" caption="Detail ID" visible={false} showInColumnChooser={false} />
                <Column dataField="RIdentity" caption="Participant" allowSorting={false} setCellValue={setParticipantValue}>
                    <RequiredRule message={CommissionMessages.ParticipantRequired} />
                    <Lookup
                        dataSource={participants}
                        valueExpr="rowid"
                        displayExpr="code"
                    />
                </Column>
                <Column
                    dataField="ParticipantName"
                    caption="Name"
                    dataType="string"
                    allowEditing={false}
                    allowSorting={false}
                />
                <Column dataField="BasisType" caption="Basis Type" allowSorting={false} setCellValue={setBasisTypeValue}>
                    <Lookup
                        dataSource={basisTypeOptions}
                        valueExpr="id"
                        displayExpr="text"
                    />
                </Column>
                <Column
                    dataField="Basis"
                    caption="Basis"
                    dataType="number"
                    format={{ type: 'fixedPoint', precision: 2 }}
                    allowSorting={false}
                    setCellValue={setBasisValue}
                />
                <Column
                    dataField="Rate"
                    caption="Rate"
                    dataType="number"
                    format={{ type: 'fixedPoint', precision: 4 }}
                    allowSorting={false}
                    setCellValue={setRateValue}
                />
                <Column
                    dataField="Amount"
                    caption="Amount"
                    dataType="number"
                    format={{ type: 'currency', precision: 2 }}
                    allowSorting={false}
                />
                <Column
                    dataField="Cumulative"
                    caption="Cumulative"
                    dataType="number"
                    format={{ type: 'currency', precision: 2 }}
                    allowEditing={false}
                    allowSorting={false}
                />
                <Column
                    dataField="Description"
                    caption="Description"
                    dataType="string"
                    allowSorting={false}
                />
                <Toolbar>
                    <Item name="addRowButton" location="after" disabled={!canEdit} />
                    <Item location="after" locateInMenu="never">
                        <Button
                            icon="trash"
                            hint="Delete selected"
                            stylingMode="text"
                            disabled={!canEdit || selectedKeys.length === 0}
                            onClick={onDeleteSelected}
                        />
                    </Item>
                    <Item name="saveButton" />
                    <Item name="revertButton" />
                    <Item name="exportButton" />
                </Toolbar>
            </DataGrid>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(CommissionsTab);
